import { useSuspenseQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

/**
 * Type for the response from the poolPrice API
 */
export type PoolPriceHistoryResponse = {
  poolId: string;
  chainId: number;
  startDate: number;
  endDate: number;
  interval: 'daily' | 'hourly';
  historicalPriceData: {
    tick: number;
    token0Price: string;
    token1Price: string;
    date: number;
  }[];
};

type PoolPriceHistoryParams = {
  chainId: number;
  poolId: string;
  startDate: number;
  endDate: number;
  interval: 'daily' | 'hourly'; // Required parameter
};

/**
 * Fetch historical price data for a pool
 * @param params - Parameters for the API call (poolId, startDate, endDate, interval)
 * @returns Historical price data for the pool
 */
async function fetchPoolPriceHistory(
  params: PoolPriceHistoryParams,
): Promise<PoolPriceHistoryResponse> {
  const { poolId, startDate, endDate, interval, chainId } = params;

  // Validate input parameters
  if (!poolId) {
    throw new Error('Pool ID is required');
  }

  if (!startDate || !endDate) {
    throw new Error('Start date and end date are required');
  }

  if (!params.chainId) {
    throw new Error('Chain ID is required');
  }

  if (!interval) {
    throw new Error('Interval is required and must be either "daily" or "hourly"');
  }

  // Call the API endpoint
  const searchParams = new URLSearchParams({
    chainId: String(chainId),
    poolId,
    startDate: String(startDate),
    endDate: String(endDate),
    interval: interval,
  });
  const url = `/api/v1/poolPriceHistory?${searchParams.toString()}`;
  const response = await fetch(url, {
    method: 'GET',
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = response.statusText;
    try {
      const parsedError = JSON.parse(errorText) as Record<string, unknown>;
      if (parsedError && 'error' in parsedError && typeof parsedError.error === 'string') {
        errorMessage = parsedError.error;
      }
    } catch {
      // Use default error message if JSON parsing fails
    }
    throw new Error(`Failed to fetch pool price history: ${errorMessage}`);
  }

  // For successful responses, parse the JSON and return the data
  const responseText = await response.text();
  try {
    const parsedData = JSON.parse(responseText) as Record<string, unknown>;

    // Validate the structure of the parsed data
    if (!parsedData || typeof parsedData !== 'object') {
      throw new Error('Invalid response format');
    }

    if (!('historicalPriceData' in parsedData) || !Array.isArray(parsedData.historicalPriceData)) {
      throw new Error('Missing or invalid historicalPriceData field');
    }

    return parsedData as PoolPriceHistoryResponse;
  } catch (e) {
    throw new Error(`Failed to parse pool price history response: ${responseText.slice(0, 100)}`);
  }
}

// Query key generator
export const QUERY_KEY_POOL_PRICE_HISTORY = (params: PoolPriceHistoryParams) => [
  'POOL_PRICE_HISTORY',
  params.chainId,
  params.poolId,
  params.startDate,
  params.endDate,
  params.interval,
];

// Query function
const QUERY_FN_POOL_PRICE_HISTORY = async (
  params: PoolPriceHistoryParams,
): Promise<PoolPriceHistoryResponse> => {
  return fetchPoolPriceHistory(params);
};

/**
 * Hook to fetch historical price data for a pool
 * @param params - Parameters for the API call including poolId, startDate, endDate, and interval (required)
 * @returns Historical price data for the pool
 */
export function usePoolPriceHistory(params: PoolPriceHistoryParams): PoolPriceHistoryResponse {
  const queryOptions = useMemo(
    () => ({
      queryKey: QUERY_KEY_POOL_PRICE_HISTORY(params),
      queryFn: async () => QUERY_FN_POOL_PRICE_HISTORY(params),
    }),
    [params],
  );

  const { data } = useSuspenseQuery(queryOptions);

  return data;
}
